import React from 'react';
import { Box, FormLabel, Stack, useTheme } from '@mui/material';
import { InsightClaimTable } from '@/components/tables/InsightClaimTable';
import { DebtorsInvoiceDataGrid } from '@/components/tables/DebtorsInvoiceTable';
import { useObjectContext } from '@/hooks/useObjectContext';
import InsightDebtorsTable from '@/components/tables/InsightDebtorsTable';

const fillParentSx = {
	flex: 1,
	minHeight: 0,
	height: '100%',
	display: 'flex',
	flexDirection: 'column',
};

const scrollAreaSx = {
	flex: 1,
	minHeight: 0,
	overflowY: 'auto',
	overflowX: 'hidden',
};

const getDataGridSx = (theme) => ({
	'& > .MuiDivider-root': {
		display: 'none',
	},
	'& .MuiDataGrid-root': {
		border: 'none !important',
		'--DataGrid-rowBorderColor': 'transparent',
	},
	'& .MuiDataGrid-cell': {
		fontSize: '13px',
		py: 0,
		border: 'none !important',
	},
	'& .MuiDataGrid-columnHeaderTitleContainerContent': {
		fontSize: '13px',
	},
	...(theme.palette.mode === 'light' && {
		'& .MuiDataGrid-container--top [role="row"], & .MuiDataGrid-container--bottom [role="row"]':
			{
				backgroundColor: theme.palette.action.hover,
			},
	}),
	'& .MuiDataGrid-columnHeader': {
		maxHeight: '38px',
		color: theme.palette.text.primary,
		border: 'none !important',
		':hover': {
			'& .MuiDataGrid-columnSeparator': {
				display: 'flex',
			},
		},
	},
	'& .MuiDataGrid-row': {
		border: 'none !important',
	},
	'& .MuiTypography-root': {
		fontSize: '13px',
	},
	'& .MuiDataGrid-columnSeparator': {
		display: 'none',
	},
});

const getTableSx = (theme) => ({
	'& .MuiTableCell-root': {
		p: '8px',
		fontSize: '13px',
	},
	'& .MuiTableBody-root .MuiTableCell-root': {
		borderBottom: 'none',
	},
	'& .MuiTableHead-root .MuiTableCell-root': {
		lineHeight: '1.5em',
		color: theme.palette.text.primary,
		backgroundColor: theme.palette.action.hover,
		fontWeight: 600,
	},
	'& .MuiPaper-root': {
		borderRadius: '5px',
		boxShadow: 'none',
		border: `1px solid ${theme.palette.divider}`,
	},
	'& .MuiTableBody-root .MuiTableRow-root': {
		':hover': {
			backgroundColor: 'action.hover',
		},
	},
	'& .MuiTableContainer-root': {
		borderRadius: '5px',
	},
});

export const CompletePolicySelection = ({ setLoading, error, fillAvailable = false }) => {
	const theme = useTheme();

	const { clientRow, dealRow } = useObjectContext();

	const isNewBusiness = dealRow?.pipeline === process.env.REACT_APP_PIPELINE_NEW_BUSINESS;
	const isClaim = dealRow?.pipeline === process.env.REACT_APP_PIPELINE_CLAIMS;
	const isEndorsement = dealRow?.pipeline === process.env.REACT_APP_PIPELINE_ENDORSEMENTS;
	const isDebtor = dealRow?.pipeline === process.env.REACT_APP_PIPELINE_DEBTORS;
	const isRenewal = dealRow?.pipeline === process.env.REACT_APP_PIPELINE_RENEWALS;

	const tableSx = getTableSx(theme);
	const dataGridSx = getDataGridSx(theme);

	if (isNewBusiness || isRenewal || isEndorsement) {
		return (
			<Stack
				sx={{
					...(fillAvailable && fillParentSx),
				}}
			>
				<InsightDebtorsTable
					clientId={clientRow?.insightId}
					sortByEffectiveDate
					displayCheckBoxes
					columnType='transaction'
					disableColumnWidth
					disableColumnMenu
					displayTransactionFilter
					transactionLabelDirection='row'
					dataGrid={{ showCellVerticalBorder: false, showColumnVerticalBorder: false }}
					sx={{
						...(fillAvailable && {
							...fillParentSx,
							'& .MuiDataGrid-parentDiv': {
								flex: 1,
								minHeight: 0,
								overflow: 'hidden',
								borderRadius: '5px',
								border: `1px solid ${error ? theme.palette.error.main : theme.palette.divider}`,
								display: 'flex',
								flexDirection: 'column',
							},
							// DataGrid sets height: 'auto' on itself; override so it fills the flex
							// parent and uses its internal scroll instead of growing forever.
							'& .MuiDataGrid-parentDiv .MuiDataGrid-root': {
								height: '100% !important',
							},
							// '& .MuiDataGrid-cell[data-field="FromToDate"] .MuiStack-root': {
							// 	flexDirection: 'row',
							// },
						}),
						...(!fillAvailable && {
							'& .MuiDataGrid-parentDiv': {
								maxHeight: '35vh',
								borderRadius: '5px',
								border: `1px solid ${error ? theme.palette.error.main : theme.palette.divider}`,
								display: 'flex',
								flexDirection: 'column',
							},
						}),
						'& .MuiGrid-root': {
							pb: 0.5,
						},
						...dataGridSx,
						'& .MuiInputBase-root': { height: 32, minHeight: 32 },
					}}
				/>
			</Stack>
		);
	}

	if (isDebtor) {
		return (
			<Stack
				sx={{
					...dataGridSx,
					...(fillAvailable && fillParentSx),
					...(!fillAvailable && {
						'& .MuiDataGrid-parentDiv': {
							maxHeight: '35vh',

							display: 'flex',
							flexDirection: 'column',
						},
					}),
				}}
			>
				<FormLabel sx={{ flexShrink: 0 }}>Invoices</FormLabel>
				<DebtorsInvoiceDataGrid disableColumnWidth disableColumnMenu />
			</Stack>
		);
	}

	if (isClaim) {
		return (
			<Box
				sx={{
					...(fillAvailable && fillParentSx),
					...tableSx,
				}}
			>
				<FormLabel sx={{ flexShrink: 0 }}>Claim</FormLabel>
				<Box sx={fillAvailable ? scrollAreaSx : undefined}>
					<InsightClaimTable setLoading={setLoading} />
				</Box>
			</Box>
		);
	}

	return <></>;
};
